import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useDialogOpenedContext } from '@sorare/core/src/contexts/dialogOpened';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useSubscription } from '@sorare/core/src/hooks/useSubscription';
import { useTimeoutCallback } from '@sorare/core/src/hooks/useTimeoutCallback';

import { MissionToast } from './MissionToast';
import type {
  CurrentUserTaskWasUpdatedSubscription,
  CurrentUserTaskWasUpdatedSubscriptionVariables,
} from './__generated__/index.graphql';

const CURRENT_USER_TASK_WAS_UPDATED_SUBSCRIPTION = gql`
  subscription CurrentUserTaskWasUpdatedSubscription {
    currentUserTaskWasUpdated {
      previousProgress
      task {
        id
        ...MissionToast_taskInterface
      }
    }
  }
  ${MissionToast.fragments.taskInterface}
` as TypedDocumentNode<
  CurrentUserTaskWasUpdatedSubscription,
  CurrentUserTaskWasUpdatedSubscriptionVariables
>;

type CurrentUserTaskWasUpdatedPayload = NonNullable<
  CurrentUserTaskWasUpdatedSubscription['currentUserTaskWasUpdated']
>;

export const CurrentUserTaskUpdatedToast = () => {
  const [accumulatedData, setAccumulatedData] = useState<
    CurrentUserTaskWasUpdatedPayload[]
  >([]);
  const [toastHasBeenClosed, setToastHasBeenClosed] = useState(false);

  const {
    flags: { useMissionToasts = false },
  } = useFeatureFlags();

  const { isIosApp } = useIsMobileApp();
  const { numberOfOpenedDialogs } = useDialogOpenedContext();
  const isDialogVisible = numberOfOpenedDialogs > 0;

  // add a delay to let some time for the exit animation to complete
  const onCloseTimeoutCallback = useTimeoutCallback(() => {
    setAccumulatedData(prev => prev.slice(1));
    setToastHasBeenClosed(false);
  }, 300);

  const currentlyVisibleToastData = accumulatedData.at(0);

  useSubscription(CURRENT_USER_TASK_WAS_UPDATED_SUBSCRIPTION, {
    skip: !useMissionToasts || isIosApp,
    onData({ data }) {
      const { currentUserTaskWasUpdated } = data.data || {};
      if (currentUserTaskWasUpdated) {
        setAccumulatedData(prev => [...prev, currentUserTaskWasUpdated]);
      }
    },
  });
  if (!currentlyVisibleToastData) {
    return null;
  }

  return (
    <MissionToast
      key={`${currentlyVisibleToastData.task.id}-${currentlyVisibleToastData.previousProgress}`}
      open={!isDialogVisible && !toastHasBeenClosed}
      onClose={() => {
        setToastHasBeenClosed(true);
        onCloseTimeoutCallback();
      }}
      task={currentlyVisibleToastData.task}
      previousProgress={currentlyVisibleToastData.previousProgress ?? 0}
      isLastToast={accumulatedData.length === 1}
    />
  );
};
